import React, { useMemo, useRef } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { ImagePlus, X } from 'lucide-react';

interface ContractTemplateEditorProps {
  content: string;
  coverImageBase64: string | null;
  onChangeContent: (value: string) => void;
  onChangeCover: (base64: string | null) => void;
  placeholder?: string;
}

const CONTRACT_PLACEHOLDERS = [
  '{legal_name}',
  '{trade_name}',
  '{company_tax_id}',
  '{address_full}',
  '{email}',
  '{phone_number}',
  '{data_emissao}',
  '{produtos}',
  '{observacoes_gerais}',
];

export function ContractTemplateEditor({
  content,
  coverImageBase64,
  onChangeContent,
  onChangeCover,
  placeholder,
}: ContractTemplateEditorProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const modules = useMemo(
    () => ({
      toolbar: [
        [{ header: [1, 2, 3, false] }],
        ['bold', 'italic', 'underline'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ align: [] }],
        ['link', 'image'],
        ['clean'],
      ],
    }),
    []
  );

  const handleCoverChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      onChangeCover(result);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleRemoveCover = () => {
    onChangeCover(null);
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Imagem de capa (logo/identidade)</Label>
        <div className="flex items-center gap-4">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleCoverChange}
            className="hidden"
          />
          {coverImageBase64 ? (
            <div className="flex items-center gap-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
              <img
                src={coverImageBase64}
                alt="Capa"
                className="max-h-20 max-w-[120px] object-contain"
              />
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <ImagePlus className="w-4 h-4 mr-1" />
                  Trocar
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleRemoveCover}
                  className="text-red-600 hover:text-red-700"
                >
                  <X className="w-4 h-4 mr-1" />
                  Remover
                </Button>
              </div>
            </div>
          ) : (
            <Button
              type="button"
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
            >
              <ImagePlus className="w-4 h-4 mr-2" />
              Adicionar imagem de capa
            </Button>
          )}
        </div>
        <p className="text-xs text-gray-500">
          A imagem aparecerá no topo do contrato (ex: logo da associação). Formatos: PNG, JPG, GIF.
        </p>
      </div>

      <div className="space-y-2">
        <Label>Conteúdo do contrato</Label>
        <div className="border border-gray-200 rounded-lg overflow-hidden [&_.quill]:border-0 [&_.ql-toolbar]:rounded-t-lg [&_.ql-container]:rounded-b-lg [&_.ql-editor]:min-h-[300px]">
          <ReactQuill
            theme="snow"
            value={content}
            onChange={onChangeContent}
            modules={modules}
            placeholder={placeholder}
          />
        </div>
        <p className="text-xs text-gray-500">
          Use os placeholders no texto: {CONTRACT_PLACEHOLDERS.join(', ')}
        </p>
      </div>
    </div>
  );
}
